// CHANGE MY PASSWORD -- every role (password-reset, owner 2026-09-30).
//
// Opened from the profile menu in the top bar. Needs the CURRENT password;
// the new one must meet the policy (min 10, re-checked by the server). The
// server signs out the user's OTHER sessions and keeps this one. Passwords
// are held only in this component's state and cleared after every attempt.

import { useState } from "react";
import { changeMyPassword } from "@/services/api";
import { MIN_PASSWORD } from "@/components/UsersTab";
import { colors, semanticColors, radius, spacing, heights, layout, typography, shadows } from "@/styles/tokens";

const INPUT: React.CSSProperties = {
  height: heights.input, padding: `0 ${spacing[8]}px`, width: "100%",
  border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
  fontSize: typography.fontSize.base, backgroundColor: semanticColors.surface,
  color: semanticColors.textPrimary, boxSizing: "border-box",
};
const BUTTON: React.CSSProperties = {
  height: heights.control, padding: `0 ${spacing[14]}px`,
  border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
  fontSize: typography.fontSize.base, backgroundColor: semanticColors.surface,
  color: semanticColors.textPrimary, cursor: "pointer",
};
const LABEL: React.CSSProperties = { fontSize: typography.fontSize.sm, color: semanticColors.textSecondary };

export function ChangePasswordModal({ onClose }: { onClose: () => void }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const submit = async () => {
    setError("");
    if (!current) { setError("Enter your current password."); return; }
    if (next.length < MIN_PASSWORD) { setError(`The new password must be at least ${MIN_PASSWORD} characters.`); return; }
    if (next !== confirm) { setError("The two new passwords do not match."); return; }
    setBusy(true);
    const result = await changeMyPassword(current, next);
    setBusy(false);
    setCurrent(""); setNext(""); setConfirm("");
    if (!result.ok) { setError(result.error ?? "Your password was not changed."); return; }
    setDone(true);
  };

  return (
    <div data-testid="change-password-backdrop"
         onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}
         style={{ position: "fixed", inset: 0, zIndex: 60, backgroundColor: semanticColors.overlay,
                  display: "flex", alignItems: "center", justifyContent: "center", padding: spacing[16] }}>
      <div data-testid="change-password-dialog" role="dialog" aria-modal="true" aria-label="Change my password"
           style={{ width: layout.modalWidth, maxWidth: "100%", backgroundColor: colors.white,
                    borderRadius: radius.base, boxShadow: shadows.modal, padding: spacing[20],
                    display: "flex", flexDirection: "column", gap: spacing[12] }}>
        <div style={{ fontSize: typography.fontSize.lg, fontWeight: typography.fontWeight.semibold, color: semanticColors.textPrimary }}>
          Change my password
        </div>
        {done ? (
          <>
            <div data-testid="change-password-done" style={LABEL}>
              Your password was changed. Your other sessions have been signed out; this one stays signed in.
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <button data-testid="change-password-close" style={BUTTON} onClick={onClose}>Close</button>
            </div>
          </>
        ) : (
          <>
            <label style={LABEL}>Current password
              <input data-testid="change-password-current" type="password" autoComplete="current-password"
                     value={current} onChange={(e) => setCurrent(e.target.value)} style={INPUT} />
            </label>
            <label style={LABEL}>New password (min {MIN_PASSWORD})
              <input data-testid="change-password-new" type="password" autoComplete="new-password"
                     value={next} onChange={(e) => setNext(e.target.value)} style={INPUT} />
            </label>
            <label style={LABEL}>Confirm new password
              <input data-testid="change-password-confirm" type="password" autoComplete="new-password"
                     value={confirm} onChange={(e) => setConfirm(e.target.value)} style={INPUT} />
            </label>
            {error && (
              <div data-testid="change-password-error" role="alert"
                   style={{ fontSize: typography.fontSize.sm, color: semanticColors.destructive }}>{error}</div>
            )}
            <div style={{ display: "flex", justifyContent: "flex-end", gap: spacing[8] }}>
              <button data-testid="change-password-cancel" style={BUTTON} disabled={busy} onClick={onClose}>Cancel</button>
              <button data-testid="change-password-submit" disabled={busy} onClick={() => void submit()}
                      style={{ ...BUTTON, border: "none", backgroundColor: colors.indigo600,
                               color: semanticColors.onAccent, fontWeight: typography.fontWeight.medium }}>
                {busy ? "Changing…" : "Change password"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
